import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiEye,FiCopy,FiCheck,FiMessageSquare,FiSparkles,FiEdit}=FiIcons;

const MessagePreview=({formData,onMessageUpdate})=> {
  const [copied,setCopied]=useState(false);
  const [isEditing,setIsEditing]=useState(false);
  const [editableMessage,setEditableMessage]=useState('');
  const [savedMessage,setSavedMessage]=useState('');

  // Convert markdown to clean text for display/copy
  const convertToCleanText=(text)=> {
    const cleanText=text
      .split('\n')
      .map(line=> {
        let processedLine=line;
        
        // Remove bold markers ** **
        processedLine=processedLine.replace(/\*\*(.*?)\*\*/g,'$1');
        
        // Remove italic markers _ _
        processedLine=processedLine.replace(/_(.*?)_/g,'$1');
        
        // Convert bullet points to checkmarks
        if (processedLine.startsWith('• ')) {
          return `✅ ${processedLine.substring(2)}`;
        }
        
        // Remove section separators (replace with empty string to keep break)
        if (processedLine.trim()==='***') {
          return '';
        }
        
        return processedLine;
      })
      // IMPORTANT: Do NOT filter empty lines here to preserve paragraph breaks
      .join('\n');
      
    // Normalize multiple newlines to max 2 to avoid huge gaps
    return cleanText.replace(/\n{3,}/g,'\n\n');
  };

  // Format vocabulary/grammar points
  const formatVocabGrammar=(text)=> {
    if (!text) return '{Vocabulary & Grammar}';
    const lines=text.split('\n').filter(line=> line.trim());
    return lines.map(line=> `• ${line.trim()}`).join('\n');
  };

  // Construct the message (Updated to remove * and _ from dynamic fields and add period after topic)
  const constructMessage=()=> {
    const student=formData.student_name || '{Student Name}';
    const topic=formData.topic || '{Topics Covered}';
    const vocab=formatVocabGrammar(formData.vocab_grammar);
    const perfContent=formData.performance_content || '{Performance Details}';
    const imp1=formData.improvement_1 || '{Improvement 1}';
    const imp2=formData.improvement_2 || '{Improvement 2}';
    const noteContent=formData.note_content || '{Note Content}';
    const signatureTitle=formData.signature_title || 'Best regards,';
    const signatureName=formData.signature_name || 'Your Teacher';

    // Removed spacing between Hello and It was very nice
    // Removed separator and spacing between Topic and New Words
    return `**Hello ${student},**
It was very nice and fun having you in class today! 👋

***

**Today we learned all about **${topic}.
**New Words / Learning Points**
${vocab}

***

**Performance**
You did ${formData.performance_level} at ${perfContent}. Well done! 🎉

***

**Future Improvements**
Next time, we will continue to work on ${imp1} and ${imp2}.

***

**${formData.note_type}**
${noteContent}

***

${signatureTitle}
${signatureName}`;
  };

  const message=constructMessage();

  // Initialize editable message
  const handleEditStart=()=> {
    // If we have a saved message, use it. Otherwise convert the current constructed message.
    setEditableMessage(savedMessage || convertToCleanText(message));
    setIsEditing(true);
  };

  // Save edited message
  const handleSaveChanges=()=> {
    setSavedMessage(editableMessage);
    if (onMessageUpdate) {
      onMessageUpdate(editableMessage);
    }
    setIsEditing(false);
  };

  // Cancel editing
  const handleCancelEdit=()=> {
    setIsEditing(false);
  };

  // Convert text to HTML for display
  const formatDisplayMessage=(text)=> {
    // Use saved message if available (which is already clean text)
    // If not, use the raw message (which has *** and **)
    const displayText=savedMessage || text;
    
    return displayText
      .split('\n')
      .map((line,index)=> {
        let formattedLine=line;
        
        // Bold text between ** and ** (only for headers in raw message)
        formattedLine=formattedLine.replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>');
        
        // Handle checkmarks (already converted in savedMessage or needing conversion)
        if (formattedLine.startsWith('✅ ')) {
          return `<div class="ml-4 mb-1 text-gray-700">✅ ${formattedLine.substring(2)}</div>`;
        }
        
        // Handle bullet points (convert to checkmarks for display)
        if (formattedLine.startsWith('• ')) {
          return `<div class="ml-4 mb-1 text-gray-700">✅ ${formattedLine.substring(2)}</div>`;
        }
        
        // Handle section separators (only in raw message) - reduced spacing
        if (formattedLine.trim()==='***') {
          return '<hr class="my-2 border-gray-200" />';
        }
        
        // Handle empty lines - Render as break to preserve spacing
        if (formattedLine.trim()==='') {
          return '<br />';
        }
        
        // Regular text - no spacing between title and paragraph content
        return `<div class="mb-0 min-h-[1.5em]">${formattedLine}</div>`;
      })
      .join('');
  };

  const handleCopy=async ()=> {
    try {
      // Always copy the clean text version (preserved breaks, no symbols)
      const textToCopy=isEditing ? editableMessage : (savedMessage || convertToCleanText(message));
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(()=> setCopied(false),2000);
    } catch (err) {
      // Fallback
      const textArea=document.createElement('textarea');
      textArea.value=isEditing ? editableMessage : (savedMessage || convertToCleanText(message));
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(()=> setCopied(false),2000);
    }
  };

  const isComplete=formData.student_name && formData.topic && formData.vocab_grammar && formData.performance_content && formData.improvement_1 && formData.improvement_2 && formData.note_content && formData.signature_title && formData.signature_name;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 sticky top-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={FiEye} className="w-6 h-6 mr-3" style={{color: '#4a8ccc'}} />
          <h2 className="text-xl font-semibold text-gray-800">Preview</h2>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing && (
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleEditStart}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiEdit} className="w-4 h-4 mr-2" />
              Edit
            </motion.button>
          )}
          
          <motion.button
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            onClick={handleCopy}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              copied ? 'bg-green-500 text-white' : 'text-white hover:opacity-90'
            }`}
            style={{backgroundColor: copied ? undefined : '#4a8ccc'}}
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4 mr-2" />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </motion.button>
        </div>
      </div>

      <div className={`p-8 rounded-lg border-2 transition-colors ${
        isComplete ? 'border-green-200 bg-gradient-to-br from-white to-green-50' : 'border-blue-100 bg-gradient-to-br from-white to-blue-50'
      }`}>
        {isEditing ? (
          <textarea
            value={editableMessage}
            onChange={(e)=> setEditableMessage(e.target.value)}
            className="w-full h-96 p-4 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:border-blue-500 outline-none font-sans text-sm leading-relaxed"
            style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            placeholder="Edit your message here..."
          />
        ) : (
          <div 
            className="text-gray-800 leading-relaxed font-sans"
            dangerouslySetInnerHTML={{__html: formatDisplayMessage(message)}}
          />
        )}
      </div>

      {isEditing && (
        <div className="mt-4 flex gap-2">
          <motion.button
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            onClick={handleCancelEdit}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            onClick={handleSaveChanges}
            className="flex-1 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-colors"
            style={{backgroundColor: '#4a8ccc'}}
          >
            Save Changes
          </motion.button>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-500">
          <SafeIcon icon={FiMessageSquare} className="w-4 h-4 mr-2" />
          {isEditing ? 
            "Editing mode - Make your changes and save" : 
            (savedMessage ? "Custom message saved!" :
            (isComplete ? "Great! All sections are ready." : "Fill in the form to complete the message."))
          }
        </div>
        {(isComplete || savedMessage) && !isEditing && (
          <motion.div
            initial={{opacity: 0,scale: 0.8}}
            animate={{opacity: 1,scale: 1}}
            className="flex items-center text-green-600 text-sm font-medium"
          >
            <SafeIcon icon={FiSparkles} className="w-4 h-4 mr-1" />
            {savedMessage ? "Custom message!" : "Ready to send!"}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MessagePreview;